import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Channel } from 'src/app/channel/model/channel';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { CaseService } from 'src/app/core/service/case.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Member } from 'src/app/member/model/member';
import { User } from 'src/app/user/model/user';

@Component({
  standalone: false,
  selector: 'app-case-enrollment',
  templateUrl: './case-enrollment.component.html',
  styleUrls: ['./case-enrollment.component.scss']
})
export class CaseEnrollmentComponent implements OnInit
{
  @Input() public member: Member;
  @Input() public channel: Channel;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public refresh: EventEmitter<Channel> = new EventEmitter();

  public channelToEnroll: Channel = null;
  public caseForm: UntypedFormGroup;

  public caseManagers: User[] = [];
  public caseManagersFiltered: User[] = [];
  public currentUser: User;
  public currentUserId: number;
  public patientLabel = {};
  public searchTerms = '';
  public creatingCase = false;

  constructor(
    private messageHandlerService: MessageHandlerService,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private router: Router,
    private caseService: CaseService,
    private toastrService: ToastrService,
    private authenticationService: AuthenticationService,
    private translate: TranslateService,
    private settingService: SettingService
  ) { }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    const user = this.authenticationService.currentUser;
    this.currentUser = user;
    this.currentUserId = user ? user.id : 0;
    // Prevent Enrolling this test member to other members
    if (this.member.name.fullName.includes('Test') && this.member.name.fullName.includes('Disregard'))
    {
      this.messageHandlerService.displayWarning('CANNOT-TEST-FEATURE');
      this.cancelling.emit();
      return;
    }

    this.getUsers(this.channel.id);
    this.channelToEnroll = this.channel;

    const managerValidator = [];
    if (this.channelToEnroll.caseManagerAssignmentMode === null)
    {
      managerValidator.push(Validators.required);
    }

    this.caseForm = this.formBuilder.group({
      enrollmentReason: [null, Validators.required],
      manager: [{ value: null, disabled: false }, managerValidator],
    });
  }

  /**
   * Creates a case for this member
   */
  public createCase(): void
  {
    const newCase =
    {
      name: `${this.member.name.fullName}`,
      channel:
      {
        id: this.channelToEnroll.id
      },
      member:
      {
        id: this.member.id
      },
      enrollmentReason: this.caseForm.controls.enrollmentReason.value,
      manager:
      {
        id: this.caseForm.controls.manager.value ? this.caseForm.controls.manager.value.id : 0,
      },
      priority: 50
    };

    // call service to create case
    this.creatingCase = true;
    this.caseService.createCase(newCase).subscribe({
      next: (data) =>
      {
        // refresh enrollment list to get the new enrollment.
        this.refresh.emit();

        // Check if has channel access before allowing modal click navigation
        if (this.currentUser.role === 'ROLE_OPERATOR' || this.isChannelAccessAllowed(this.channelToEnroll))
        {
          const newCaseId = data.id;
          this.translate.get('CASE-SAVE-SUCCESS-WITH-NAVIGATE').subscribe({
            next: (res) =>
            {
              this.toastrService.success(res, null,
                {
                  enableHtml: true,
                  timeOut: 5000
                })
                .onTap.subscribe({
                  next: () =>
                  {
                    this.openCase(newCaseId);
                  }
                });
            }
          });
        }
        else
        {
          this.messageHandlerService.displaySuccess('CASE-SAVE-SUCCESS');
        }
      },
      error: () =>
      {
        this.messageHandlerService.displayError('CASE-SAVE-ERROR');
        this.creatingCase = false;
        this.close();
      },
      complete: () =>
      {
        this.creatingCase = false;
        // remove this channel from not enrolled channel list
        this.refresh.emit(this.channelToEnroll);
        this.close();
      }
    });
  }

  public isChannelAccessAllowed(dataItem: any): boolean
  {
    if (dataItem && dataItem.users && dataItem.users.length > 0)
    {
      return dataItem.users.indexOf(this.currentUserId) >= 0;
    }
    else
    {
      if (dataItem.assignee && this.currentUser.userName === dataItem.assignee)
      {
        return true;
      }
      else
      {
        return false;
      }
    }
  }

  /**
   * This method navigate to a specific Case Id
   *
   * @param caseId Case Id to navigate to.
   */
  public openCase(caseId: number): void
  {
    this.router.navigate([`/case/${caseId}`]);
  }

  public close(): void
  {
    this.cancelling.emit();
  }

  /**
   *  Check if user from dropdown is disabled or enabled.
   *
   *  @returns boolean
   * */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Filters the channel staff based on user input.
   */
  public handleCaseManagerFilter(filter: string): void
  {
    this.searchTerms = filter;
    this.caseManagersFiltered = this.caseManagers
      .filter(manager => manager.name.toLowerCase().includes(filter.toLowerCase()));
  }

  private initManagerLists(value)
  {
    const sorted = sortBy(value, ['name']);
    this.caseManagers = sorted;
    this.caseManagersFiltered = sorted;
  }

  private getUsers(channelId): void
  {
    this.userService.getUsersByChannelGroups(channelId).subscribe({
      next: (data) =>
      {
        this.initManagerLists(data);
      }
    });
  }
}
